import { useCallback, useEffect, useMemo, useState } from 'react'
import { LineChart } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  BottomSheet,
  Button,
  TradeHistoryPanel,
  type Trade,
} from '@/components'
import { botApi, type BacktestEventRaw } from '../api'
import { toUnixSeconds } from '../adapters/eventFetcher'

interface BotTradesProps {
  btid: string
  onTimeClick?: (timestamp: number) => void
}

const DEFAULT_PAGE_SIZE = 25
// Matches BotDetail.jsx's isMobile threshold (< 1100). On desktop a row click
// jumps the chart; on mobile (hidden columns) it opens the detail drawer.
const MOBILE_QUERY = '(max-width: 1099px)'

function formatDateTime(iso: string): string {
  const dot = iso.indexOf('.')
  let base = dot >= 0 ? iso.slice(0, dot) : iso
  if (base.endsWith('Z')) base = base.slice(0, -1)
  // Drop seconds: keep "YYYY-MM-DD HH:MM" (matches the "T" separator too).
  const match = base.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/)
  return match ? `${match[1]} ${match[2]}` : base
}

interface BotTrade extends Trade {
  pctGain: number
  /** Exits the position took before its final close (0 before engine revision 4). */
  partialExits: number
}

const LOTS_DECIMALS = 2
const PRICE_DECIMALS = 5

function formatLots(n: number | null | undefined): string {
  if (n == null) return '—'
  return n.toFixed(LOTS_DECIMALS)
}

function formatPrice(n: number | null | undefined): string | undefined {
  if (n == null) return undefined
  return n.toFixed(PRICE_DECIMALS)
}

function eventToTrade(e: BacktestEventRaw): BotTrade | null {
  const p = e.payload
  if (!p) return null
  const entryTime = p.entry_time
  const exitTime = p.exit_time
  if (!entryTime || !exitTime) return null
  if (p.pnl == null || p.balance == null) return null

  // Engine API 3 reports the balance the position was entered on. Before that, the balance
  // after the close minus its P&L was the pre-trade balance — true while one position at a
  // time moved the account, not once another position's close or a partial exit did.
  const preBalance = typeof p.entry_balance === 'number' && Number.isFinite(p.entry_balance)
    ? p.entry_balance
    : p.balance - p.pnl
  const pct = preBalance !== 0 ? (p.pnl / preBalance) * 100 : 0
  const dir = (p.type || 'BUY').toUpperCase() === 'SELL' ? 'SELL' : 'BUY'

  return {
    id: e.event_id,
    dir,
    vol: formatLots(p.volume),
    entry: formatPrice(p.entry_price) ?? '—',
    // Volume-weighted over every exit when the position closed in parts; the final fill otherwise.
    exitP: formatPrice(p.avg_exit_price ?? p.exit_price) ?? '—',
    sl: formatPrice(p.sl),
    tp: formatPrice(p.tp),
    pnl: p.pnl,
    win: p.pnl >= 0,
    openDate: formatDateTime(entryTime),
    closeDate: formatDateTime(exitTime),
    // The engine writes '+00:00' offsets; appending 'Z' to those gave NaN (toUnixSeconds reads both).
    entryTime: toUnixSeconds(entryTime),
    exitTime: toUnixSeconds(exitTime),
    gain: `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`,
    pctGain: pct,
    partialExits: p.partial_exits_count ?? (Array.isArray(p.partial_exits) ? p.partial_exits.length : 0),
  }
}

interface DetailEntry {
  label: string
  value: string
  tone?: 'success' | 'danger' | 'neutral'
  strong?: boolean
}

function buildDetailEntries(t: BotTrade): DetailEntry[] {
  const pnlPositive = t.pnl >= 0
  const pctPositive = t.pctGain >= 0
  return [
    { label: 'Open Time', value: t.openDate },
    { label: 'Close Time', value: t.closeDate },
    { label: 'Type', value: t.dir },
    { label: 'Lots', value: String(t.vol) },
    { label: 'Entry', value: String(t.entry) },
    { label: 'Exit', value: String(t.exitP) },
    ...(t.partialExits > 0 ? [{ label: 'Partial exits', value: String(t.partialExits) }] : []),
    { label: 'SL', value: t.sl != null ? String(t.sl) : '—' },
    { label: 'TP', value: t.tp != null ? String(t.tp) : '—' },
    {
      label: '$ Gain',
      value: `${pnlPositive ? '+' : ''}${t.pnl.toFixed(2)}`,
      tone: pnlPositive ? 'success' : 'danger',
      strong: true,
    },
    {
      label: '% Gain',
      value: t.gain ?? '—',
      tone: pctPositive ? 'success' : 'danger',
    },
  ]
}

const TONE_CLASS: Record<NonNullable<DetailEntry['tone']>, string> = {
  success: 'text-success',
  danger: 'text-danger',
  neutral: 'text-foreground',
}

interface BotTradeDetailSheetProps {
  trade: BotTrade | null
  open: boolean
  onClose: () => void
  onTimeClick?: (timestamp: number) => void
}

function BotTradeDetailSheet({
  trade,
  open,
  onClose,
  onTimeClick,
}: BotTradeDetailSheetProps) {
  const entries = useMemo(
    () => (trade ? buildDetailEntries(trade) : []),
    [trade],
  )
  const handleViewOnChart = () => {
    if (!trade?.entryTime || !onTimeClick) return
    onTimeClick(trade.entryTime)
    onClose()
  }
  const showFooter = !!(trade && onTimeClick)

  return (
    <BottomSheet
      open={open && trade !== null}
      onClose={onClose}
      ariaLabel={trade ? `Trade ${trade.closeDate}` : 'Trade details'}
    >
      {trade && (
        <div className="flex flex-col h-full min-h-0">
          <div className="shrink-0 px-4 pt-2 pb-3">
            <h2 className="text-section text-foreground">
              Trade
              <span className="text-label text-muted-foreground ml-2">
                {trade.closeDate}
              </span>
            </h2>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4">
            <div className="flex flex-col gap-2">
              {entries.map((entry) => (
                <div
                  key={entry.label}
                  className="flex justify-between border-b border-border py-2"
                >
                  <span className="text-label text-muted-foreground">
                    {entry.label}
                  </span>
                  <span
                    className={cn(
                      'text-body',
                      TONE_CLASS[entry.tone ?? 'neutral'],
                      entry.strong && 'font-semibold',
                    )}
                  >
                    {entry.value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {showFooter && (
            <div className="shrink-0 flex gap-2 p-4 border-t border-border bg-card">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={handleViewOnChart}
              >
                <LineChart size={12} strokeWidth={2} />
                View on chart
              </Button>
            </div>
          )}
        </div>
      )}
    </BottomSheet>
  )
}

interface PrevQuery {
  btid: string
  page: number
  pageSize: number
}

export default function BotTrades({ btid, onTimeClick }: BotTradesProps) {
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [events, setEvents] = useState<BacktestEventRaw[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [selectedTrade, setSelectedTrade] = useState<BotTrade | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(MOBILE_QUERY).matches : false,
  )

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY)
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  // "Store information from previous renders" pattern so we can react to btid /
  // page / pageSize changes (reset rows, flip loading=true) without calling
  // setState synchronously inside a useEffect.
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [prevQuery, setPrevQuery] = useState<PrevQuery | null>(null)
  if (
    prevQuery === null ||
    prevQuery.btid !== btid ||
    prevQuery.page !== page ||
    prevQuery.pageSize !== pageSize
  ) {
    const btidChanged = prevQuery?.btid !== btid
    setPrevQuery({ btid, page, pageSize })
    setLoading(true)
    setErrorMessage(null)
    if (btidChanged) {
      setEvents([])
      setTotal(0)
      if (page !== 0) setPage(0)
    }
  }

  useEffect(() => {
    let cancelled = false
    botApi
      .getBacktestClosedTrades(btid, page + 1, pageSize)
      .then((data) => {
        if (cancelled) return
        setEvents(data.events)
        setTotal(data.total)
      })
      .catch((err) => {
        if (cancelled) return
        const message =
          err && typeof err === 'object' && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Failed to load trades'
        setErrorMessage(message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [btid, page, pageSize])

  const trades = useMemo<BotTrade[]>(() => {
    return events
      .map(eventToTrade)
      .filter((t): t is BotTrade => t !== null)
  }, [events])

  const handleRowClick = useCallback(
    (trade: Trade) => {
      const t = trade as BotTrade
      if (isMobile) {
        setSelectedTrade(t)
        setSheetOpen(true)
        return
      }
      if (onTimeClick && t.entryTime != null) {
        onTimeClick(t.entryTime)
      }
    },
    [isMobile, onTimeClick],
  )

  const handleSheetClose = useCallback(() => {
    setSheetOpen(false)
  }, [])

  if (errorMessage) {
    return (
      <div className="flex items-center justify-center h-full px-6 text-body text-danger text-center">
        {errorMessage}
      </div>
    )
  }

  if (loading && events.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-body text-muted-foreground gap-2">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current" />
        <span>Loading trades...</span>
      </div>
    )
  }

  return (
    <>
      <TradeHistoryPanel
        trades={trades}
        hideSubTabs
        hidePips
        page={page}
        totalRows={total}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(0)
        }}
        onRowClick={handleRowClick}
        isEmpty={total === 0}
        emptyLabel="No trades in this backtest yet"
        emptyMessage="No trades in this backtest yet"
      />
      <BotTradeDetailSheet
        trade={selectedTrade}
        open={sheetOpen}
        onClose={handleSheetClose}
        onTimeClick={onTimeClick}
      />
    </>
  )
}
